import { useState } from "react";
import { Scales, DownloadSimple, Warning, Info, CheckCircle, ArrowRight, Share, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Callout({ type, children }) {
  const styles = {
    warning: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300",
    info: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300",
    success: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300",
  };
  const icons = { warning: Warning, info: Info, success: CheckCircle };
  const Icon = icons[type] || Info;
  return (
    <div className={`flex gap-3 p-3 rounded-xl border text-sm ${styles[type]}`}>
      <Icon size={18} weight="duotone" className="shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

function AccordionItem({ title, badge, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white dark:bg-slate-800 text-left hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800 dark:text-white text-sm">{title}</span>
          {badge && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
              {badge}
            </span>
          )}
        </div>
        <span className="text-slate-400 text-xl leading-none font-light">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-5 py-5 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 space-y-4 text-sm text-slate-600 dark:text-slate-300">
          {children}
        </div>
      )}
    </div>
  );
}

// Shared content — used both inside the app layout and the public standalone page
export function LegalContent() {
  const [downloading, setDownloading] = useState(false);

  const downloadAgreement = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/legal/rental-agreement-template`);
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hostfolio-rental-agreement.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Agreement template downloaded!");
    } catch { toast.error("Download failed. Please try again."); }
    finally { setDownloading(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText("https://hostfolio.in/legal");
    toast.success("Link copied!");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Scales size={28} className="text-amber-500" weight="duotone" />
            Legal Guide for Hosts
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Everything you need to host legally and protect yourself in India.
          </p>
        </div>
        <button
          onClick={downloadAgreement}
          disabled={downloading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm transition-all shadow-sm disabled:opacity-60"
        >
          <DownloadSimple size={16} weight="bold" />
          {downloading ? "Generating…" : "Download Rental Agreement Template"}
        </button>
      </div>

      {/* Quick Reference */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "GST Threshold", value: "₹20 Lakh", sub: "annual turnover" },
          { label: "GST Rate ≥ ₹7,500/night", value: "18%", sub: "accommodation" },
          { label: "GST Rate ₹1K–7.5K/night", value: "12%", sub: "accommodation" },
          { label: "Police Verification", value: "24 hrs", sub: "after check-in" },
        ].map(s => (
          <div key={s.label} className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-center">
            <p className="text-xl font-bold font-mono text-amber-500">{s.value}</p>
            <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 mt-0.5">{s.label}</p>
            <p className="text-[10px] text-slate-400">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <AccordionItem title="GST Compliance for Hosts" badge="Tax">
          <Callout type="info">
            You must register for GST if your annual rental income exceeds <strong>₹20 lakh</strong> (₹10 lakh for North-East states). Registration is free at gst.gov.in.
          </Callout>
          <div className="space-y-2">
            <p><strong>GST rates for accommodation:</strong></p>
            <ul className="space-y-1.5 ml-4">
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span>Nightly rate <strong>below ₹1,000</strong>: Exempt (0% GST)</span></li>
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span>Nightly rate <strong>₹1,000 to ₹7,499</strong>: 12% GST</span></li>
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span>Nightly rate <strong>₹7,500 or above</strong>: 18% GST</span></li>
            </ul>
          </div>
          <p>GST must be collected from guests, filed monthly (GSTR-1) or quarterly, and deposited by the 20th of each month. HostFolio auto-calculates and displays GST on every booking.</p>
          <Callout type="warning">
            Renting through platforms like Airbnb: Airbnb collects and deposits GST on your behalf for bookings made through their platform. For direct bookings, you are responsible.
          </Callout>
          <p><strong>TDS:</strong> If a booking platform pays you over ₹2.4 lakh/year, they must deduct 2% TDS. You can claim this in your ITR.</p>
        </AccordionItem>

        <AccordionItem title="Police Verification of Guests" badge="Legal">
          <Callout type="warning">
            Under the Model Tenancy Act and local police regulations in most Indian states, hosts must submit guest identification to the local police station within <strong>24 hours</strong> of check-in for stays of any duration.
          </Callout>
          <p><strong>What to collect from guests:</strong></p>
          <ul className="space-y-1.5 ml-4">
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span>Government-issued photo ID (Aadhaar, Passport, Driving Licence)</span></li>
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span>Permanent address matching the ID</span></li>
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span>Mobile number (for verification)</span></li>
          </ul>
          <p><strong>How to submit:</strong> Most cities now have an online portal (e.g. Delhi: citizenportal.delhi.gov.in, Maharashtra: tenants form at local police station). Check with your local police station for the exact process.</p>
          <Callout type="info">
            Failing to register guests can result in fines of ₹500–5,000 and in repeat cases, property sealing. Many experienced hosts send an Aadhaar collection message automatically on booking.
          </Callout>
        </AccordionItem>

        <AccordionItem title="Rental Agreement Essentials" badge="Contracts">
          <p>Even for short-term stays, a signed rental agreement protects both parties. It establishes:</p>
          <ul className="space-y-1.5 ml-4 mt-2">
            <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" /><span>The guest's obligation to vacate on the agreed date</span></li>
            <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" /><span>Security deposit terms and conditions for its return</span></li>
            <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" /><span>House rules as legally enforceable terms</span></li>
            <li className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" /><span>Jurisdiction for dispute resolution</span></li>
          </ul>
          <Callout type="success">
            Download the HostFolio rental agreement template above — it covers all essential clauses for Indian short-term rentals and is court-admissible when signed by both parties.
          </Callout>
          <p><strong>Registration:</strong> Agreements over 11 months must be registered with the Sub-Registrar. Short-term stays (under 11 months) do not require registration but should be stamped (₹100–500 stamp paper).</p>
          <p><strong>Digital signatures</strong> via DigiLocker or Aadhaar eSign are legally valid under the IT Act 2000 and are admissible in court.</p>
        </AccordionItem>

        <AccordionItem title="Property Damage — Claims & Recovery" badge="Protection">
          <p><strong>Security deposit:</strong> Always collect a refundable security deposit (typically 1–2× nightly rate) before check-in. This is your first line of defence.</p>
          <Callout type="info">
            Collect security deposits through HostFolio's direct booking page — the deposit is held and released only when you confirm everything is intact. This avoids UPI disputes.
          </Callout>
          <p><strong>If damage occurs:</strong></p>
          <ol className="space-y-1.5 ml-4 list-decimal">
            <li>Document damage with timestamped photos within 2 hours of check-out</li>
            <li>Get a written repair quote from a contractor</li>
            <li>Notify the guest in writing (WhatsApp message is sufficient evidence)</li>
            <li>Deduct from security deposit and refund the balance within 3 working days</li>
            <li>If damage exceeds deposit, file a claim in Consumer Court or Police (for malicious damage)</li>
          </ol>
          <p><strong>Platform claims (Airbnb):</strong> Airbnb's AirCover provides up to $3M USD in damage protection. You must file within 14 days of check-out via the Resolution Center with photos and quotes.</p>
        </AccordionItem>

        <AccordionItem title="Overstaying Guests — Legal Eviction" badge="High Risk">
          <Callout type="warning">
            Forcibly removing a guest — changing locks, removing belongings, or cutting power — is illegal in India under Section 441 of the IPC (criminal trespass by the evicted party). Always follow legal process.
          </Callout>
          <p><strong>Correct process for overstaying guests:</strong></p>
          <ol className="space-y-1.5 ml-4 list-decimal">
            <li>Send a written notice (WhatsApp counts) stating the agreed check-out date and requesting immediate vacation</li>
            <li>Wait 24 hours — document all communication</li>
            <li>If guest refuses, file an FIR at the local police station citing trespass</li>
            <li>Police can direct guest to vacate without a court order in short-term stay cases where a rental agreement is in place</li>
            <li>For extended disputes, file for eviction in the Rent Control Tribunal</li>
          </ol>
          <p><strong>Prevention is better:</strong> Collect a security deposit that covers extra nights. Add a clause in your rental agreement that overstaying beyond 24 hours of check-out incurs ₹5,000/day penalty.</p>
        </AccordionItem>

        <AccordionItem title="Short-Term Rental Insurance" badge="Protection">
          <p>Standard home insurance policies typically <strong>exclude</strong> rental activities. A guest injury or theft during your rental could leave you personally liable for lakhs.</p>
          <p><strong>Options available in India:</strong></p>
          <ul className="space-y-1.5 ml-4 mt-2">
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span><strong>Bajaj Allianz Home Insurance</strong> — offers "rental property" endorsement. Call 1800-209-0144 to add rider.</span></li>
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span><strong>HDFC ERGO Home Shield</strong> — covers tenant liability and loss of rent. Apply online at hdfcergo.com.</span></li>
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-amber-500 mt-0.5 shrink-0" /><span><strong>Airbnb AirCover</strong> — $3M USD host liability + $3M USD damage protection included free for all Airbnb listings.</span></li>
          </ul>
          <Callout type="info">
            Typical premium for STR insurance rider: ₹3,000–8,000 per year for a 1BHK. Compare this to the cost of one major damage incident or liability claim.
          </Callout>
          <p><strong>What to cover:</strong> Public liability (guest injury), structural damage, contents theft, loss of rental income due to insured event.</p>
        </AccordionItem>
      </div>

      {/* Host-to-host sharing */}
      <div className="rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-600 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <p className="font-semibold text-slate-800 dark:text-white text-sm flex items-center gap-2">
            <span className="text-base">🏡</span> Know another Airbnb host?
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Most hosts don't know about GST compliance or their legal obligations. Share this guide with your host group — it could save them a lot of trouble.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={copyLink}
            className="flex items-center gap-1.5 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-3 py-2 rounded-xl transition">
            <Copy size={13} weight="bold" /> Copy link
          </button>
          <a href={`https://wa.me/?text=${encodeURIComponent("If you're an Airbnb host in India, this legal guide covers GST, police verification, rental agreements and more: https://hostfolio.in/legal")}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-xl transition">
            <Share size={13} weight="bold" /> Share on WhatsApp
          </a>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-5 text-sm text-amber-800">
        <p className="font-semibold mb-1">Disclaimer</p>
        <p>This guide is for informational purposes only and does not constitute legal advice. Laws vary by state and change over time. Consult a qualified lawyer for advice specific to your situation and jurisdiction.</p>
      </div>
    </div>
  );
}

// Standalone public page — used at /legal for non-logged-in visitors
export default function LegalHub() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <a href="https://hostfolio.in" className="font-manrope text-lg font-bold">
            <span className="text-slate-800">Host</span><span className="text-amber-500">Folio</span>
          </a>
          <a href="https://hostfolio.in/login"
            className="text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg transition">
            Try Free →
          </a>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <LegalContent />
      </div>
    </div>
  );
}
